
import React, { useState, useRef } from "react"
import {
	Button,
	Grid,
	List,
	ListItem,
	TextField,
	Typography,
	InputLabel,
	Link,
	IconButton,
	InputAdornment
} from "@material-ui/core"
import {
	Add,
	Delete as DeleteIcon,
	Edit as EditIcon,
	GetApp as GetAppIcon,
	Search as SearchIcon,
	Close as CloseIcon
} from "@material-ui/icons"

import {
	Divider,
	ActionDialog,
	Notification,
	Loading,
	Portlet,
	PopConfirm,
	ChatMessageBuilder
} from "@/components"

import useCustomStyles from "@/styles/custom"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/QuickReply/styles"

import AdminQuickReplySkeleton from "@/skeletons/Admin/QuickReply"

import { ChatMessageBuilderHandler } from "@/components/ChatMessageBuilder"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"

import { BuildedMessage, IQuickReply } from "@/protocols/messages"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import AnalyticsService from "@/services/Analytics"

import { runAfterReactRender } from "@/utils/node"
import { Variable } from "@/components/ChatMessageBuilder/VariableInput"
import MoreOptionsMenu from "@/components/MoreOptionsMenu"
import Breadcrumb from "@/components/BreadcrumbNew"
import { letalkLinks } from "@/utils/link"
import HardCoded from "@/services/HardCoded"
import ImportQuickRepliesDialog from "@/pages/Admin/QuickReply/ImportQuickRepliesDialog"
import useDebounce from "@/hooks/useDebounce"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"
import QuickReplyGroupPermissionGate from "@/components/PermissionGate/QuickReplyGroupPermissionGate"
import { useGlobalStateStore } from "@/store/GlobalState"

type DialogType = "create" | "edit"

type QuickReplyDataProps = {
	openDialog: DialogType | null
	quick_reply_id?: number
	shortcut_name?: string
	content?: {
		messages: BuildedMessage[]
	}
}

type QuickReplyUpdateDataProps = Omit<QuickReplyDataProps, "openDialog">

export type QuickReplySearchQuery = {
	shortcutName: string
}

const QuickReply: React.FC = () => {
	const [quickReplyData, setQuickReplyData] = useState<QuickReplyDataProps>({
		openDialog: null
	})

	const [quickReplyLoading, setQuickReplyLoading] = useState<boolean>(false)
	const [variables, setVariables] = useState<Variable[]>([])

	const [quickReplies, setQuickReplies] = useState<IQuickReply[]>([])
	const [quickRepliesLoading, setQuickRepliesLoading] = useState<boolean>(true)
	const [searchQuery, setSearchQuery] = useState<QuickReplySearchQuery>()
	const canImportQuickReplies = HardCoded.checkFeatureFlag("canImportQuickReplies") || HardCoded.checkFeatureFlag("canAdminImportQuickReplies")

	const handleSearchChange = (searchText: string) => {
		setSearchQuery({ shortcutName: searchText })
	}

	const chatMessageBuilderRef = useRef<ChatMessageBuilderHandler>(null)

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const globalStateStore = useGlobalStateStore()

	const { checkPermission } = usePermissionStateStore()

	const { triggerValidation, validation } = useValidation()

	const handleQuickReplyOpen = (dialogType: DialogType, editData?: QuickReplyUpdateDataProps) => {
		setQuickReplyData({
			...editData,
			openDialog: dialogType
		})

		if (dialogType === "create") {
			runAfterReactRender(() => {
				chatMessageBuilderRef.current?.createMessage()
			})
		}
	}

	const handleQuickReplyClose = () => {
		setQuickReplyData({
			openDialog: null
		})

		setQuickReplyLoading(false)
	}

	const handleQuickReplyDataChange = (key: keyof QuickReplyDataProps, value: string) => {
		setQuickReplyData(lastState => ({
			...lastState,
			[key]: value
		}))
	}

	const getQuickReplies = async () => {
		try {
			setQuickRepliesLoading(true)
			const { data } = await ApiService.get("/quick-reply", {
				params: {
					quick_reply_shortcut_name: searchQuery?.shortcutName
				}
			})

			setQuickReplies(data)
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setQuickRepliesLoading(false)
	}

	const handleDeleteQuickReply = async (quickReplyId: number) => {
		PopConfirm.open({
			title: "Excluir Resposta Rápida",
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText: "EXCLUIR",
			onConfirm: async () => {
				try {
					await ApiService
						.delete(`/quick-reply/${quickReplyId}`)

					await getQuickReplies()

					Notification.success({ message: "Resposta rápida deletada com sucesso!" })
				} catch (error) {
					triggerValidation(error as ErrorType)
				}
			}
		})
	}

	const handleQuickReplySave = async () => {
		setQuickReplyLoading(true)

		const messages = chatMessageBuilderRef.current?.getMessages()

		try {
			if (quickReplyData.openDialog === "create") {
				const result = await ApiService.post("/quick-reply", {
					shortcut_name: quickReplyData.shortcut_name || "",
					content: { messages }
				})

				const quickReply = result.data as IQuickReply

				handleQuickReplyOpen("edit", {
					content: quickReply.content,
					quick_reply_id: quickReply.id,
					shortcut_name: quickReply.shortcut_name
				})

				Notification.success({ message: "A mensagem foi criada com sucesso!" })
			} else {
				await ApiService.put(`/quick-reply/${quickReplyData.quick_reply_id}`, {
					shortcut_name: quickReplyData.shortcut_name || "",
					content: { messages }
				})

				Notification.success({ message: "A mensagem foi editada com sucesso!" })
			}

			handleQuickReplyClose()

			await getQuickReplies()
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			triggerValidation(error as ErrorType)
		}

		setQuickReplyLoading(false)
	}

	const getAvailableVariables = async () => {
		try {
			const response = await ApiService.get("/quick-reply/variables", {
				params: {
					instanceId: globalStateStore.instance.instance_id
				}
			})

			const variables = response.data.map((variable: { title: string, value: string }) => ({
				title: (
					<p>
						{variable.title && (
							<>
								<small>{variable.title}</small>
							</>
						)}
					</p>
				),
				value: variable.value
			}))
			setVariables(variables)
		} catch (err) {
			ErrorHandlerService.handle(err as ErrorType)
		}
	}

	const setImportedQuickReplies = (importedQuickReplies: IQuickReply[]) => {
		setQuickReplies((lastState) => {
			return [
				...lastState,
				...importedQuickReplies
			]
		})
	}

	useDebounce(
		async () => {
			getQuickReplies()
		},
		searchQuery?.shortcutName,
		2250
	)

	useDidMount(() => {
		AnalyticsService.capture("quick_replies_opened")
		getQuickReplies()
		getAvailableVariables()
	})

	return (
		<Grid container direction="column" spacing={5}>
			<Grid item>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Breadcrumb
							data={[
								{ name: "Respostas Rápidas", pathname: "/admin/quick-reply" }
							]}
						/>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={1}>
							<Grid item style={{ height: 100 }}>
								<Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
									<Grid item>
										<Typography
											variant="h5"
											className={classes.titleText}
										>
											Respostas Rápidas
										</Typography>
									</Grid>
									<Grid item>
										<Grid container direction="row" spacing={2}>
											<Grid item>
												<TextField
													value={searchQuery?.shortcutName}
													onChange={({ target }) =>
														handleSearchChange(target.value)
													}
													placeholder="Pesquisar"
													variant="outlined"
													InputProps={{
														startAdornment: (
															<InputAdornment style={{ minHeight: 52 }} position="start">
																<SearchIcon />
															</InputAdornment>
														),
														endAdornment: searchQuery?.shortcutName && (
															<IconButton
																size="small"
																onClick={() => handleSearchChange("")}
															>
																<CloseIcon
																	fontSize="small"
																/>
															</IconButton>
														)
													}}
													fullWidth
													size="small"
													helperText={validation.search}
													error={!!validation.search}
												/>
											</Grid>
											<Grid item>
												<QuickReplyGroupPermissionGate action="create">
													<Button
														variant="contained"
														color="primary"
														onClick={() => handleQuickReplyOpen("create")}
														startIcon={<Add />}
													>
														Adicionar Resposta Rápida
													</Button>
												</QuickReplyGroupPermissionGate>
											</Grid>
											{canImportQuickReplies && <Grid item>
												<QuickReplyGroupPermissionGate action="create">
													<ImportQuickRepliesDialog onChange={(importedQuickReplies) => setImportedQuickReplies(importedQuickReplies)}>
														<Button
															variant="contained"
															className={classes.buttonPrimaryColorOutLined}
															color="primary"
															startIcon={<GetAppIcon />}
														>
															Importar Resposta Rápida
														</Button>
													</ImportQuickRepliesDialog>
												</QuickReplyGroupPermissionGate>
											</Grid>}
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Grid container justifyContent="space-between" direction="row" spacing={2}>
									<Grid item>
										<Link
											href={letalkLinks.wikiHowToCreateQuickReply}
											underline="always"
											target="_blank"
											color="inherit"
										>
											Otimize seu atendimento com respostas pré-criadas
										</Link>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid item>
				<Loading
					loading={quickRepliesLoading}
					customLoadingElement={<AdminQuickReplySkeleton />}>
					<List disablePadding>
						{
							quickReplies.length
								? quickReplies
									.sort((replyA: IQuickReply, replyB: IQuickReply) => replyA.shortcut_name.toLowerCase() < replyB.shortcut_name.toLowerCase() ? -1 : 1)
									.map((quickReply, index) => (
										<ListItem disableGutters key={index}>
											<Portlet elevation={1}>
												<Grid
													container
													justify="space-between"
													alignItems="flex-start"
													className={classes.quickReplyCard}
													spacing={6}
												>
													<Grid item xs>
														<Grid
															container
														>
															<Grid item xs>
																<Typography
																	variant="h4"
																>
																	{quickReply.shortcut_name}
																</Typography>
																<Divider orientation="horizontal" size={1} />
																<Typography
																	color="textSecondary"
																	variant="body2"
																>
																#{quickReply.id}
																</Typography>
															</Grid>
														</Grid>
													</Grid>

													<Grid item xs={6}>
														<Grid container justifyContent="flex-end">
															{quickReply?.content?.messages?.map(message => (
																<Grid
																	item
																	key={message.id}
																>
																	<Grid
																		container
																	>
																		<MessageItem
																			type={message.type}
																			content={message.content}
																			mediaName={message.mediaName}
																			extraData={message.extraData}
																		/>
																	</Grid>
																</Grid>
															))}
														</Grid>
													</Grid>

													<Grid item>
														<Grid container justify="flex-end">
															<Grid item>
																<Grid container className={classes.iconButton} justifyContent="center" alignItems="center">
																	<MoreOptionsMenu
																		options={[
																			{
																				title: "Alterar",
																				icon: <EditIcon />,
																				showIcon: true,
																				disabled: !checkPermission("quick-reply", "update"),
																				disabledTooltip: PERMISSION_DENIED_MESSAGE,
																				onClick: async () => {
																					handleQuickReplyOpen("edit", {
																						quick_reply_id: quickReply.id,
																						shortcut_name: quickReply.shortcut_name,
																						content: quickReply.content
																					})
																				}
																			},
																			{
																				title: "Excluir",
																				icon: <DeleteIcon />,
																				showIcon: true,
																				color: "danger",
																				disabled: !checkPermission("quick-reply", "delete"),
																				disabledTooltip: PERMISSION_DENIED_MESSAGE,
																				onClick: async () => { handleDeleteQuickReply(quickReply.id) }
																			}
																		]}
																	/>
																</Grid>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											</Portlet>
										</ListItem>
									))
								: <Grid item xs={12}>
									<Typography align="center" variant="h2" className={classes.titleText}>
									Nenhuma resposta rápida criada
									</Typography>
								</Grid>
						}
					</List>
				</Loading>
			</Grid>

			<ActionDialog
				title={`${quickReplyData.openDialog === "create" ? "Criar nova " : "Editar"} resposta rápida`}
				saveText={quickReplyData.openDialog === "create" ? "CRIAR" : "SALVAR"}
				openDialog={Boolean(quickReplyData.openDialog)}
				onClose={handleQuickReplyClose}
				onSave={handleQuickReplySave}
				loading={quickReplyLoading}
				maxWidth="lg"
				fullWidth
			>
				<Grid
					container
					spacing={4}
				>
					<Grid
						item
						xs={12}
						md={6}
					>
						<Grid
							container
							spacing={4}
							direction="column"
						>
							<Grid
								item
								xs={12}
							>
								<InputLabel
									className={customClasses.inputLabel}
								>
									Título
								</InputLabel>

								<Divider orientation="horizontal" size={1} />

								<TextField
									value={quickReplyData.shortcut_name}
									onChange={({ target }) => handleQuickReplyDataChange("shortcut_name", target.value)}
									variant="outlined"
									placeholder="Título"
									fullWidth
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid
						item
						xs={12}
						md={6}
					>
						<Grid
							container
						>
							<InputLabel
								className={customClasses.inputLabel}
							>
								Mensagem
							</InputLabel>

							<Divider orientation="horizontal" size={1} />

							<ChatMessageBuilder
								disabledInputs={["quick-reply", "mentions", "custom-link", "ai-equivalent-variation"]}
								ref={chatMessageBuilderRef}
								messageLimit={1}
								initialMessages={quickReplyData?.content?.messages || []}
								variables={variables}
							/>
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>
		</Grid>
	)
}

export default QuickReply
